import type { FlightAlertsResponse } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { Card, Dot, type DotVariantProps } from '@web/ui/core';

type AlertTilesProps = {
  counts: FlightAlertsResponse['counts'];
};

type TileTone = NonNullable<DotVariantProps['tone']>;

interface TileSpec {
  key: keyof FlightAlertsResponse['counts'];
  label: string;
  tone: TileTone;
}

/**
 * Four count tiles — always driven by the server's unfiltered `counts`
 * (never `rows.length`), so the numbers stay stable while the operator
 * changes filters. Tone order follows the spec: act red, watch amber,
 * VIP brand, unnotified amber.
 */
export function AlertTiles({ counts }: Readonly<AlertTilesProps>): React.JSX.Element {
  const tiles: TileSpec[] = [
    { key: 'act', label: t('alerts.tiles.act'), tone: 'red' },
    { key: 'watch', label: t('alerts.tiles.watch'), tone: 'amber' },
    { key: 'vip', label: t('alerts.tiles.vip'), tone: 'brand' },
    { key: 'unnotified', label: t('alerts.tiles.unnotified'), tone: 'amber' },
  ];

  return (
    <div className="grid grid-cols-4 gap-3">
      {tiles.map((tile) => (
        <Card key={tile.key}>
          <div className="flex flex-col gap-1.5 p-4">
            <span className="inline-flex items-center gap-1.5 text-meta text-muted-foreground">
              <Dot tone={tile.tone} size="md" />
              {tile.label}
            </span>
            <span className="text-display-lg font-medium tabular-nums text-foreground">
              {counts[tile.key]}
            </span>
          </div>
        </Card>
      ))}
    </div>
  );
}
